// Lost & Found — log found items with photo, finder and returned-to-guest workflow.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Package, PackageCheck, Plus, RotateCcw, Trash2, Truck, X } from 'lucide-react';
import { API_URL, apiCall, getToken, readCookie } from './_shared';

const STATUS_CLS = {
  found: 'bg-amber-100 text-amber-800', returned: 'bg-emerald-100 text-emerald-800',
  shipped: 'bg-sky-100 text-sky-800', disposed: 'bg-slate-200 text-slate-600',
};
const EMPTY = { item: '', category: 'other', unit_label: '', found_where: '', found_by: '', guest_name: '', description: '', storage_location: '' };

const uploadPhoto = async (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  const csrf = readCookie('csrf_token');
  const r = await fetch(`${API_URL}/api/admin/lost-found/${id}/photo`, {
    method: 'POST', body: fd, credentials: 'include',
    headers: { Authorization: `Bearer ${getToken()}`, ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.detail || 'Upload failed');
  return d;
};

const ItemCard = ({ it, canLead, onChange }) => {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const act = async (fn) => { setBusy(true); try { await fn(); await onChange(); } catch (e) { alert(e.message); } finally { setBusy(false); } };
  const setStatus = (status) => {
    let returned_to;
    if (status === 'returned' || status === 'shipped') {
      returned_to = window.prompt(status === 'shipped' ? 'Shipped to (name / address):' : 'Returned to (guest name):', it.guest_name || '');
      if (returned_to === null) return;
    }
    act(() => apiCall(`/api/admin/lost-found/${it.id}`, { method: 'PUT', body: JSON.stringify({ status, returned_to }) }));
  };
  return (
    <li className="bg-white border rounded-xl p-3 flex gap-3" data-testid={`lf-item-${it.id}`}>
      <button onClick={() => fileRef.current?.click()} className="w-20 h-20 rounded-lg border bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0 hover:bg-slate-100" title="Add / replace photo" data-testid={`lf-photo-${it.id}`}>
        {it.photo_url ? <img src={`${API_URL}${it.photo_url}`} alt={it.item} className="w-full h-full object-cover" /> : <Camera className="w-5 h-5 text-slate-400" />}
      </button>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`lf-photo-input-${it.id}`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) act(() => uploadPhoto(it.id, f)); e.target.value = ''; }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 truncate">{it.item} <span className="text-xs font-normal text-slate-400">· {it.category}</span></p>
            <p className="text-xs text-slate-600">
              {it.unit_label ? `Room ${it.unit_label}` : it.room_name || it.found_where || 'Location n/a'}
              {it.found_where && it.unit_label ? ` · ${it.found_where}` : ''} · found {it.found_on} by <b>{it.found_by}</b>
              {it.guest_name && <> · guest <b>{it.guest_name}</b></>}
              {it.storage_location && <> · stored: {it.storage_location}</>}
            </p>
            {it.description && <p className="text-xs text-slate-500 mt-0.5">{it.description}</p>}
            {it.returned_at && <p className="text-xs text-emerald-700 mt-0.5">✓ {it.status} to <b>{it.returned_to}</b> on {it.returned_at.slice(0, 10)} by {it.returned_by}</p>}
          </div>
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${STATUS_CLS[it.status]}`} data-testid={`lf-status-${it.id}`}>{it.status}</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {it.status === 'found' && (
            <>
              <button onClick={() => setStatus('returned')} disabled={busy} className="px-2 py-1 rounded bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1 hover:bg-emerald-700" data-testid={`lf-return-${it.id}`}><PackageCheck className="w-3 h-3" /> Returned to guest</button>
              <button onClick={() => setStatus('shipped')} disabled={busy} className="px-2 py-1 rounded border text-xs font-semibold flex items-center gap-1 hover:bg-slate-50" data-testid={`lf-ship-${it.id}`}><Truck className="w-3 h-3" /> Shipped</button>
              <button onClick={() => { if (window.confirm('Mark as disposed?')) setStatus('disposed'); }} disabled={busy} className="px-2 py-1 rounded border text-xs text-slate-600 hover:bg-slate-50" data-testid={`lf-dispose-${it.id}`}>Disposed</button>
            </>
          )}
          {it.status !== 'found' && (
            <button onClick={() => setStatus('found')} disabled={busy} className="px-2 py-1 rounded border text-xs flex items-center gap-1 hover:bg-slate-50" data-testid={`lf-reopen-${it.id}`}><RotateCcw className="w-3 h-3" /> Reopen</button>
          )}
          {canLead && (
            <button onClick={() => { if (window.confirm('Delete this item?')) act(() => apiCall(`/api/admin/lost-found/${it.id}`, { method: 'DELETE' })); }} disabled={busy} className="ml-auto p-1 rounded hover:bg-red-50" title="Delete" data-testid={`lf-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
          )}
        </div>
      </div>
    </li>
  );
};

const LostFoundPanel = ({ userRole }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('found');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const canLead = ['super_admin', 'admin', 'manager'].includes(userRole);

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams({ status });
      if (q.trim()) p.set('q', q.trim());
      setData(await apiCall(`/api/admin/lost-found?${p}`));
    } catch (e) { alert(e.message); }
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.item.trim()) return;
    setSaving(true);
    try {
      const it = await apiCall('/api/admin/lost-found', { method: 'POST', body: JSON.stringify(form) });
      if (file) await uploadPhoto(it.id, file);
      setForm(EMPTY); setFile(null); setShowNew(false); setStatus('found');
      await load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const f = (k, ph, extra = {}) => (
    <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className="border rounded-lg px-3 py-2 text-sm" data-testid={`lf-new-${k}`} {...extra} />
  );
  const chips = ['found', 'returned', 'shipped', 'disposed', 'all'].map((s) => (
    <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1 rounded-full text-xs font-semibold ${status === s ? 'bg-slate-900 text-white' : 'bg-white border text-slate-700 hover:bg-slate-50'}`} data-testid={`lf-filter-${s}`}>
      {s}{data?.counts && s !== 'all' ? ` (${data.counts[s] || 0})` : ''}
    </button>
  ));
  const items = (data?.items || []).map((it) => <ItemCard key={it.id} it={it} canLead={canLead} onChange={load} />);

  return (
    <div className="space-y-3" data-testid="lost-found-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Package className="w-5 h-5 text-orange-500" /> Lost &amp; Found</h3>
        <div className="flex gap-1.5 flex-wrap">{chips}</div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item, guest, room…" className="border rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[160px]" data-testid="lf-search" />
        <button onClick={() => setShowNew((v) => !v)} className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center gap-1" data-testid="lf-add">
          {showNew ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showNew ? 'Cancel' : 'Log item'}
        </button>
      </div>
      {showNew && (
        <div className="bg-white border rounded-xl p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="lf-new-form">
          {f('item', 'Item (e.g. black iPhone charger) *')}
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="lf-new-category">
            {(data?.categories || ['other']).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {f('unit_label', 'Room number (e.g. 716)')}
          {f('found_where', 'Where exactly (bedside, pool bar…)')}
          {f('found_by', 'Found by (defaults to you)')}
          {f('guest_name', 'Guest name (if known)')}
          {f('storage_location', 'Stored at (reception safe, HK office…)')}
          <label className="border rounded-lg px-3 py-2 text-sm text-slate-600 flex items-center gap-2 cursor-pointer hover:bg-slate-50">
            <Camera className="w-4 h-4" /> {file ? file.name.slice(0, 22) : 'Photo'}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="lf-new-photo" />
          </label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description / notes" rows={2} className="border rounded-lg px-3 py-2 text-sm sm:col-span-2 lg:col-span-3" data-testid="lf-new-description" />
          <button onClick={create} disabled={saving || !form.item.trim()} className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50" data-testid="lf-new-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Save item
          </button>
        </div>
      )}
      <ul className="space-y-2" data-testid="lf-list">
        {items.length ? items : <li className="bg-white border rounded-xl p-8 text-center text-sm text-slate-400">No {status === 'all' ? '' : status} items.</li>}
      </ul>
    </div>
  );
};

export default LostFoundPanel;
